import { Reducer } from 'react';
import { CandidatesAction, CandidatesState } from './types';
import { candidateGroupTableColumn } from './CandidateGroupTableRows';
import {
  addAllPageItems,
  addSelected,
  removeAllSelectedItems,
  removeSelected
} from '../Components/GenericTable/tableHelper';

const PROGRESS_STEP = 0.5;
const incrementProgress = (state: CandidatesState, payload: { targetProgress: number }) => {
  const newProgress = Math.min(state.progress + PROGRESS_STEP, payload.targetProgress);
  return { ...state, progress: newProgress };
};

const startSearch = (state: CandidatesState, payload: { typesToExpand: string[] }) => {
  const { typesToExpand } = payload;
  return {
    ...state,
    showResults: false,
    progressVisible: true,
    progress: 0,
    fetchingAssessedPercentage: false,
    typesToExpand,
    totalSteps: typesToExpand.length + 2
  };
};

const completeExecutingSearch = (state: CandidatesState) => {
  const { totalSteps } = state;
  const progress = ((totalSteps - 1) * 100) / totalSteps;

  return {
    ...state,
    showResults: true,
    fetchingAssessedPercentage: true,
    optionsCleared: false,
    progress
  };
};

const completeProgress = (state: CandidatesState) => {
  return {
    ...state,
    progressVisible: false,
    progress: 100
  };
};

export const InitialCandidatesState: CandidatesState = {
  snackbar: {
    message: '',
    state: 'success'
  },
  candidatesTableState: {
    canScroll: [false, true],
    tableHovered: false,
    density: 'Default',
    columns: candidateGroupTableColumn,
    selectedItemIds: [],
    selectedItems: [],
    actions: {
      selectedItem: null,
      anchorEl: null,
      isOpen: {
        delete: false,
        edit: false
      }
    },
    modalsOpen: { edit: false }
  },
  candidatesTableSettings: {
    page: Number(sessionStorage.getItem(`candidatesTablePage`)) || 1,
    limit: 10,
    sort: 'firstname',
    sort_order: 'asc'
  },
  searchQuery: '',
  totalPages: 0,
  candidatesCount: 0,
  optionsCleared: false,
  isJobDialogOpen: false,
  typesToExpand: [],
  progress: 0,
  currentStep: '',
  totalSteps: 1,
  transitioningStep: false,
  progressVisible: false,
  fetchingAssessedPercentage: false,
  showResults: true,
  isAddToCandidatePoolOpen: false
};

export const CandidatesReducer: Reducer<CandidatesState, CandidatesAction> = (
  state: CandidatesState,
  action: CandidatesAction
) => {
  switch (action.type) {
    case 'SET_SNACKBAR':
      return { ...state, snackbar: action.payload };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        candidatesTableSettings: { ...state.candidatesTableSettings, page: 1 },
        searchQuery: action.payload
      };
    case 'SET_DENSITY':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          density: action.payload
        }
      };
    case 'SET_CAN_SCROLL':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          canScroll: action.payload
        }
      };
    case 'SET_TABLE_HOVERED':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          tableHovered: action.payload
        }
      };
    case 'SET_SORT':
      return {
        ...state,
        candidatesTableSettings: {
          ...state.candidatesTableSettings,
          sort: action.payload.sortBy,
          sort_order: action.payload.sortOrder
        }
      };
    case 'SET_PAGE':
      return {
        ...state,
        candidatesTableSettings: {
          ...state.candidatesTableSettings,
          page: action.payload
        }
      };
    case 'SET_LIMIT':
      return {
        ...state,
        candidatesTableSettings: {
          ...state.candidatesTableSettings,
          limit: action.payload
        }
      };
    case 'SELECT_ACTION_ITEM':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          actions: {
            ...state.candidatesTableState.actions,
            selectedItem: action.payload.item,
            anchorEl: action.payload.anchorEl,
            isOpen: {
              ...state.candidatesTableState.actions.isOpen,
              [action.payload.name]: true
            }
          }
        }
      };
    case 'ADD_SELECTED_ITEM':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          ...addSelected(
            state.candidatesTableState.selectedItems,
            state.candidatesTableState.selectedItemIds,
            action.payload
          )
        }
      };
    case 'REMOVE_SELECTED_ITEM':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          ...removeSelected(
            state.candidatesTableState.selectedItems,
            state.candidatesTableState.selectedItemIds,
            action.payload
          )
        }
      };
    case 'ADD_ALL_PAGE_ITEMS':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          ...addAllPageItems(state.candidatesTableState.selectedItems, action.payload)
        }
      };
    case 'SET_SELECTED_ITEM_IDS':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          selectedItemIds: action.payload
        }
      };
    case 'REMOVE_ALL_PAGE_ITEMS':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          ...removeAllSelectedItems(
            state.candidatesTableState.selectedItems,
            state.candidatesTableState.selectedItemIds
          )
        }
      };
    case 'REMOVE_ALL_SELECTED_ITEMS':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          selectedItemIds: [],
          selectedItems: []
        }
      };
    case 'CLOSE_ACTION_ITEM':
      return {
        ...state,
        candidatesTableState: {
          ...state.candidatesTableState,
          actions: {
            ...state.candidatesTableState.actions,
            selectedItem: null,
            isOpen: {
              ...state.candidatesTableState.actions.isOpen,
              [action.payload]: false
            },
            anchorEl: null
          }
        }
      };
    case 'SET_TOTAL_PAGES':
      return { ...state, totalPages: action.payload };
    case 'SET_CANDIDATES_COUNT':
      return { ...state, candidatesCount: action.payload };
    case 'SET_PROGRESS':
      return { ...state, progress: action.payload };
    case 'SET_OPTIONS_CLEARED':
      return { ...state, optionsCleared: action.payload };
    case 'SET_JOB_DIALOG_OPEN':
      return { ...state, isJobDialogOpen: action.payload };
    case 'SET_TYPES_TO_EXPAND':
      return { ...state, typesToExpand: action.payload };
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_TRANSITIONING_STEP':
      return { ...state, transitioningStep: action.payload };
    case 'SET_PROGRESS_VISIBLE':
      return { ...state, progressVisible: action.payload };
    case 'INCREMENT_PROGRESS':
      return incrementProgress(state, action.payload);
    case 'COMPLETE_PROGRESS':
      return completeProgress(state);
    case 'START_SEARCH':
      return startSearch(state, action.payload);
    case 'COMPLETE_EXECUTING_SEARCH':
      return completeExecutingSearch(state);
    case 'OPEN_ADD_TO_POOL_DIALOG':
      return { ...state, isAddToCandidatePoolOpen: true };
    case 'CLOSE_ADD_TO_POOL_DIALOG':
      return { ...state, isAddToCandidatePoolOpen: false };
    default:
      return state;
  }
};
